'use strict';

import './style.css';
import './instrument.js';
import SDKInternal from './models/Plugin';
import { AdType } from './constants/AdType';
import type { iG8Plugin, PluginOptions } from './types';

class G8PluginImpl implements iG8Plugin {
  public readonly AdType = AdType;
  private _sdkInternal: SDKInternal | null = null;

  // Additional properties that might be added by the SDK
  environment?: any;

  private checkSDKInitialized(): void {
    if (!this._sdkInternal) {
      throw new Error('[G8Plugin] Error: SDK not initialized. Please call init() method first.');
    }
  }

  init = (options: PluginOptions): void => {
    this._sdkInternal = new SDKInternal(options);
    this.environment = this._sdkInternal.getEnvironment();
  };

  ready = (): Promise<void> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.sdkReady;
  };

  destroy = (): Promise<void> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.destroy();
  };

  play = (): Promise<void> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.play();
  };

  preloadAd = (placement: string): Promise<void> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.preloadAd(placement);
  };

  showAd = (placement?: string, params?: any): Promise<void> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.showAd(placement, params);
  };

  cancelAd = (): Promise<void> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.cancelAd();
  };

  resumeGameAfterExit = (): Promise<void> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.resumeGameAfterExit();
  };

  openConsole = (): void => {
    this.checkSDKInitialized();
    this._sdkInternal!.openConsole();
  };

  setExpanded = (expanded: boolean): void => {
    this.checkSDKInitialized();
    this._sdkInternal!.setExpanded(expanded);
  };

  getPlayer = (options: { signed?: boolean } = { signed: false }): Promise<any> => {
    this.checkSDKInitialized();
    const normalizedOptions = { signed: options.signed ?? false };
    return this._sdkInternal!.getPlayer(normalizedOptions);
  };

  getLeaderboards = (): Promise<any> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.getLeaderboards();
  };

  getAchievements = (): Promise<any> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.getAchievements();
  };

  getPayments = (): Promise<any> => {
    this.checkSDKInitialized();
    return this._sdkInternal!.getPayments();
  };

  getRecentGames = (): any[] => {
    const recentGameStr = localStorage.getItem('user:recentGames');
    const recentGames = recentGameStr != null ? JSON.parse(recentGameStr) : {};
    return Object.entries(recentGames)
      .map(([, value]) => value)
      .sort((a: any, b: any) => b.lastPlayTime - a.lastPlayTime);
  };

  deleteUserData = (): void => {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i) || '';
      if (key.startsWith('user:')) {
        localStorage.removeItem(key);
      }
    }
  };

  deletePlayerData = async (): Promise<void> => {
    const dbs = await indexedDB.databases();
    dbs.forEach(({ name }) => {
      if (name?.startsWith('g8plugin')) {
        indexedDB.deleteDatabase(name);
      }
    });
  };

  getPlayerLocal = async (): Promise<any[]> => {
    const databases = await indexedDB.databases();
    const promises = databases
      .filter((db) => db.name?.startsWith('g8plugin'))
      .map((db) => {
        return new Promise((resolve, reject) => {
          if (!db.name) {
            resolve(null);
            return;
          }
          const openRequest = indexedDB.open(db.name);
          openRequest.onsuccess = function (event: any) {
            const db = event.target.result;
            const objectStoreNames = Array.from(db.objectStoreNames).filter(
              (storename) => storename === 'USER_DATA',
            );
            objectStoreNames.forEach((storeName) => {
              const transaction = db.transaction(storeName, 'readonly');
              const objectStore = transaction.objectStore(storeName);
              const g8player = objectStore.get('g8-player');
              g8player.onsuccess = function () {
                if (g8player.result) resolve(g8player.result);
              };
            });
          };
          openRequest.onerror = (event: any) => {
            reject(event.target.error);
          };
        });
      });
    return await Promise.all(promises);
  };

  getReactedLocal = (): any[] => {
    const gameReactedStr = localStorage.getItem('user:gameReacted');
    const gameReacteds = gameReactedStr != null ? JSON.parse(gameReactedStr) : {};
    return Object.values(gameReacteds);
  };

  getFavoriteLocal = (): any[] => {
    const gameFavoriteStr = localStorage.getItem('user:gameFavorite');
    const gameFavorites = gameFavoriteStr != null ? JSON.parse(gameFavoriteStr) : {};
    return Object.values(gameFavorites);
  };
}

// Export as global for browser usage
(window as any).g8plugin = new G8PluginImpl();

// Export the class for module usage
export { G8PluginImpl };
